import { useState } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { ConnectedProps, connect } from "react-redux";
import {
  DarkMode,
  LightMode,
  Password as PasswordIcon,
  Person,
  Visibility,
  VisibilityOff
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Tooltip
} from "@mui/material";
import { useTranslation } from "_metronic/i18n/language";
import { VersionApp } from "components";
import { Formik } from "formik";
import queryString from "querystring";
import { WrappedComponentProps, injectIntl } from "react-intl";
import styled from "styled-components";
import { login } from "../../crud/auth.crud";
import LanguageSelector from "../../partials/layout/LanguageSelector";
import * as auth from "../../store/ducks/auth.duck";
import Loading from "../home/components/Loading";
import ForgotPassword from "./ForgotPassword";
import RegisterOld from "./Register/indexOld";
import UserLogin from "./UserLogin";
import Toast from "./UserLogin/components/Toast";
import { useThemeApp } from "_metronic/materialUIThemeProvider/ThemeProvider";
import "./Login.css";

const ModalContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  height: 100%;
  position: relative;
  margin: auto;
  outline: none;
`;

interface FormValues {
  email: string;
  password: string;
}

type PropsFromRedux = ConnectedProps<typeof connector>;
type RouterProps = RouteComponentProps;
type Props = PropsFromRedux & RouterProps & WrappedComponentProps;

const Login: React.FC<Props> = props => {
  const translate = useTranslation();  const [isLoading, setIsLoading] = useState(false);
  const [isUserLogged, setIsUserLogged] = useState(false);
  const [error] = useState<string | null>(null);
  const { toggleTheme, isDarkMode } = useThemeApp();
  const [, setLoadingButtonStyle] = useState({
    paddingRight: "2.5rem"
  });
  const [showPassword, setShowPassword] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  // const themeClass = isDarkMode ? "dark-mode" : "light-mode";

  const handleClickShowPassword = () => setShowPassword(show => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const enableLoading = () => {
    setIsLoading(true);
    setLoadingButtonStyle({ paddingRight: "3.5rem" });
  };

  const disableLoading = () => {
    setIsLoading(false);
    setLoadingButtonStyle({ paddingRight: "2.5rem" });
  };

  const isLoginExpired = () => {
    const query = queryString.parse(window.location.search.replace("?", ""));
    return query && Object.prototype.hasOwnProperty.call(query, "expired");
  };
  function checkIsUser(): Promise<boolean> {
    return new Promise(resolve => {
      setTimeout(() => {
        const authData = localStorage.getItem("persist:auth");
        if (authData) {
          const json = JSON.parse(authData);
          const user = JSON.parse(json.user);
          resolve(user.profile.nome === "POSTNEWS");
        } else {
          resolve(false);
        }
      }, 2000);
    });
  }

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  window.setPageTitle(translate("labels_login"));

  return (
    <>      <Loading isLoading={isLoading} />
      <div className={`login-form-container `}>
        <div className="login-topbar">
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title={isDarkMode ? "Modo claro" : "Modo escuro"}>
              <IconButton
                className="theme-toggle-button"
                aria-label="toggle theme"
                edge="start"
                onClick={toggleTheme}
                sx={{ marginRight: 1 }}
              >
                {isDarkMode ? <LightMode /> : <DarkMode />}
              </IconButton>
            </Tooltip>
            <LanguageSelector iconType="" />
          </Box>
        </div>
        <div className="login-title">
          <h3>{translate("screens.login.title")}</h3>
        </div>

        <Formik
          initialValues={{
            email: "",
            password: ""
          }}
          validate={(values: FormValues) => {
            const errors: Partial<FormValues> = {};
            if (!values.email) {
              errors.email = translate("screens.login.validations.required");
            } else if (
              !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
            ) {
              errors.email = translate("screens.login.validations.invalid");
            }

            if (!values.password) {
              errors.password = translate(
                "screens.login.validations.required"
              );
            }
            return errors;
          }}
          onSubmit={(values, { setStatus, setSubmitting }) => {
            enableLoading();
            login(values.email, values.password)
              .then(async authData => {
                props.login(authData);

                const isUser = await checkIsUser();
                if (isUser) {
                  setIsLoading(false);
                  setSubmitting(false);
                  setIsUserLogged(true);
                  setStatus("");
                } else {
                  disableLoading();
                  props.history.push("/admin");
                }
              })              .catch(() => {
                disableLoading();
                setSubmitting(false);
                setToastMessage(translate("screens.login.validations.loginInvalid"));
                setToastOpen(true);
              });
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting
          }) => (
            <form
              noValidate
              autoComplete="off"
              className="login-form"
              onSubmit={handleSubmit}
            >
              {isLoginExpired() && (
                <div className="alert-message">
                  <div className="alert-text">
                    {translate("screens.login.validations.sessionExpired")}
                  </div>
                </div>
              )}

              {error && (
                <div className="alert-message">
                  <div className="alert-text">{error}</div>
                </div>
              )}

              <TextField
                className={`login-input-field `}
                type="email"
                label={translate("screens.login.email")}
                fullWidth
                variant="outlined"
                margin="normal"
                name="email"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.email}
                helperText={touched.email && errors.email}
                error={Boolean(touched.email && errors.email)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                className={`login-input-field`}
                type={showPassword ? "text" : "password"}
                fullWidth
                variant="outlined"
                margin="normal"
                label={translate("screens.login.password")}
                name="password"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.password}
                helperText={touched.password && errors.password}
                error={Boolean(touched.password && errors.password)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PasswordIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />              <Button
                type="submit"
                variant="contained"
                className="login-button"
                disabled={isSubmitting}
                fullWidth
                disableElevation
              >
                {isSubmitting ? 'Processando...' : translate("screens.login.login")}
              </Button>

              <div className="login-actions">
                <ForgotPassword />
                <RegisterOld />
              </div>

              <Modal
                open={isUserLogged}
                onClose={() => setIsUserLogged(false)}
                aria-labelledby="simple-modal-title"
                aria-describedby="simple-modal-description"
              >
                <ModalContainer>
                  <UserLogin
                    bg="white"
                    closeModal={() => setIsUserLogged(false)}
                  />
                </ModalContainer>
              </Modal>

              <div className="footer-links">
                <VersionApp />
                <Link
                  to="/privacy-policy"
                  className="privacy-link"
                >
                  {translate("defaultMessages.privacyPolicy")}
                </Link>
              </div>
            </form>
          )}
        </Formik>
      </div>
      <Toast
        open={toastOpen}
        message={toastMessage}
        type={'error'}
        onClose={handleCloseToast}
      />
    </>
  );
};

const connector = connect(null, auth.actions);
export default injectIntl(connector(Login));
